import { useEffect, useState } from "react";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

const DISMISS_KEY = "hammie-pwa-install-dismissed";

function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    ("standalone" in window.navigator &&
      (window.navigator as Navigator & { standalone?: boolean }).standalone === true)
  );
}

function isIosSafariInstallable() {
  const ua = window.navigator.userAgent.toLowerCase();
  const isIos = /iphone|ipad|ipod/.test(ua);
  const isSafari = ua.includes("safari") && !ua.includes("crios") && !ua.includes("fxios");
  return isIos && isSafari && !isStandalone();
}

export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (isStandalone()) return;
    const onBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setDeferredPrompt(event as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
  }, []);

  if (isStandalone()) return null;

  const showIosHint = !deferredPrompt && isIosSafariInstallable();
  const showInstallBanner = !dismissed && (!!deferredPrompt || showIosHint);
  if (!showInstallBanner) return null;

  const handleDismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch { /* quota or private browsing */ }
  };

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    handleDismiss();
  };

  return (
    <div className="install-notice" role="status" aria-live="polite">
      <span>
        {deferredPrompt
          ? "Install Hammie Budget for faster access from your home screen."
          : "Tap Share then \u201cAdd to Home Screen\u201d to install Hammie Budget."}
      </span>
      <span className="install-notice-actions">
        {deferredPrompt ? <button onClick={handleInstall}>Install app</button> : null}
        <button className="btn-ghost" onClick={handleDismiss}>
          Not now
        </button>
      </span>
    </div>
  );
}
